import { useAuth } from '../auth.jsx';
import { getLocale } from './i18n.js';

// 'locale' follows the language on screen (getLocale(), set by auth.jsx).
const PATTERNS = {
  locale: undefined,
  iso: 'sv-SE', // 2026-10-06 14:05
  us: 'en-US',
  eu: 'en-GB',
};

/** Formats dates the way the user chose in their preferences (date format and time zone). */
export function makeFormatDate({ date_format: format = 'locale', timezone = 'auto' } = {}) {
  const formatter = new Intl.DateTimeFormat(PATTERNS[format] ?? getLocale(), {
    dateStyle: format === 'iso' ? 'short' : 'medium',
    timeStyle: 'short',
    ...(timezone && timezone !== 'auto' ? { timeZone: timezone } : {}),
  });
  return (value, fallback = '') => (value ? formatter.format(new Date(value)) : fallback);
}

/** The formatter for the signed-in user's preferences, made once per change of preferences. */
let current = { key: null, format: makeFormatDate() };
function formatterFor(preferences) {
  const key = `${preferences?.date_format ?? 'locale'}|${preferences?.timezone ?? 'auto'}|${getLocale()}`;
  if (current.key !== key) current = { key, format: makeFormatDate(preferences ?? {}) };
  return current.format;
}

export function useFormatDate() {
  const { me } = useAuth() ?? {};
  return formatterFor(me?.preferences);
}

/**
 * The same format for plain helpers outside components (table cell renderers): the preferences of
 * the user the app last rendered for, so every date on a page reads alike.
 */
export function formatDate(value, fallback = '') {
  return current.format(value, fallback);
}

/**
 * The full date and time with the time zone, in the user's chosen format and zone (or the
 * browser's): "19 Oct 2026, 03:42 BST", "Oct 19, 2026, 3:42 AM EDT", "2026-10-19 03:42 CEST". For
 * the activity log, where "when exactly, and in which zone" matters.
 */
export function makeFormatDateTime({ date_format: format = 'locale', timezone = 'auto' } = {}) {
  const formatter = new Intl.DateTimeFormat(PATTERNS[format] ?? getLocale(), {
    year: 'numeric',
    month: format === 'iso' ? '2-digit' : 'short',
    day: format === 'iso' ? '2-digit' : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    ...(timezone && timezone !== 'auto' ? { timeZone: timezone } : {}),
  });
  return (value, fallback = '') => {
    if (!value) return fallback;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? fallback : formatter.format(d);
  };
}

let currentFull = { key: null, format: null };
export function useFormatDateTime() {
  const { me } = useAuth() ?? {};
  const preferences = me?.preferences;
  const key = `${preferences?.date_format ?? 'locale'}|${preferences?.timezone ?? 'auto'}|${getLocale()}`;
  if (currentFull.key !== key) currentFull = { key, format: makeFormatDateTime(preferences ?? {}) };
  return currentFull.format;
}

const RELATIVE_STEPS = [
  ['year', 365 * 86_400],
  ['month', 30 * 86_400],
  ['week', 7 * 86_400],
  ['day', 86_400],
  ['hour', 3600],
  ['minute', 60],
];

/** "5 minutes ago", "in 2 hours", "just now": how long ago `value` was, in the browser's language. */
export function relativeTime(value, now = Date.now(), locale = getLocale()) {
  if (!value) return '';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return '';
  const seconds = Math.round((t - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of RELATIVE_STEPS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.trunc(seconds / size), unit);
  }
  return rtf.format(0, 'second');
}

/**
 * A short duration in words: 45 → "45 s", 240 → "4 min", 90 → "1 min 30 s", 3600 → "1 h", 5400 → "1 h 30 min".
 * Zero parts are left out; 0 (or nothing) is "0 s".
 */
export function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const min = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts = [h ? `${h} h` : '', min ? `${min} min` : '', s ? `${s} s` : ''].filter(Boolean);
  return parts.length ? parts.join(' ') : '0 s';
}
